import React, { Component } from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import InvoiceHeader from './InvoiceHeader';
import LineItems from './LineItems';
import { postRequest, putRequest, deleteRequest } from '../../requestUtils';
import { analyticsTrack } from '../../analyticsUtils';
import { sum, sumTime } from '../../arrayUtils';
import './Invoice.scss';

class Invoice extends Component {
  constructor(props) {
    super(props);
    const { invoice } = props;
    this.state = {
      consulting: invoice.line_items.consulting || [],
      travel: invoice.line_items.travel || [],
      expense: invoice.line_items.expense || [],
      updated: false,
    };
    this.getDate = this.getDate.bind(this);
    this.handleNewLineItem = this.handleNewLineItem.bind(this);
    this.handleUpdateLineItem = this.handleUpdateLineItem.bind(this);
    this.handleDeleteLineItem = this.handleDeleteLineItem.bind(this);
    this.handleUploadFile = this.handleUploadFile.bind(this);
  }

  getDate() {
    const { invoice: { end_date } } = this.props;
    const thisMonth = window.moment().format('M');
    const thatMonth = window.moment(end_date).format('M');

    return thisMonth !== thatMonth ? window.moment(end_date).toDate() : new Date();
  }

  handleNewLineItem(category) {
    const { csrfToken, invoice } = this.props;
    this.setState({
      updated: false,
    });
    postRequest(
      `/invoices/${invoice.resource_id}/line_items`,
      {
        line_item: { category, date: this.getDate() },
      },
      csrfToken,
    ).then((response) => {
      this.setState({
        [category]: update(this.state[category], { $push: [response.data] }),
        updated: true,
      });
    });
  }

  handleUpdateLineItem(id, data) {
    const { csrfToken, invoice } = this.props;
    this.setState({
      updated: false,
    });
    putRequest(
      `/invoices/${invoice.resource_id}/line_items/${id}`,
      { line_item: data },
      csrfToken,
    ).then((response) => {
      const item = response.data;
      this.setState({
        [item.category]: this.state[item.category].map((el) => (el.id === item.id ? item : el)),
        updated: true,
      });
    });
  }

  handleDeleteLineItem(id, category) {
    const { csrfToken, invoice } = this.props;
    this.setState({
      updated: false,
    });
    deleteRequest(
      `/invoices/${invoice.resource_id}/line_items/${id}`,
      csrfToken,
    ).then(() => {
      analyticsTrack('Delete line item', { location });
      this.setState({
        [category]: this.state[category].filter((el) => el.id !== id),
        updated: true,
      });
    });
  }

  handleUploadFile(id, file) {
    if (file?.category) {
      this.setState({
        [file.category]: this.state[file.category].map((el) => (el.id === file.id ? file : el)),
        updated: true,
      });
    }
  }

  render() {
    const {
      projectName,
      projectResourceId,
      invoice,
      company,
      csrfToken,
      location,
      workCommitment,
      areaOfExpertises,
      tags,
      jobTitles,
      description,
      responsibilities,
      duration,
      workLocation,
      shortOfficeLocation,
      startDate,
      showHourWarning,
    } = this.props;
    const {
      consulting, travel, expense, updated,
    } = this.state;
    return (
      <div className="invoice">
        <div className="width-1200">
          <InvoiceHeader
            invoiceNumber={invoice.invoice_number}
            id={invoice.resource_id}
            abbreviatedMonth={invoice.abbreviated_month}
            month={invoice.month}
            year={invoice.year}
            status={invoice.status}
            statusDisplay={invoice.status_display}
            projectResourceId={projectResourceId}
            projectName={projectName}
            consultantName={invoice.consultant_name}
            consultantDueDate={invoice.due_date}
            memo={invoice.memo}
            projectedHours={invoice.projected_hours_by_consultants}
            totals={{
              consulting: sum(consulting),
              travel: sum(travel),
              expense: sum(expense),
              total: sum(consulting) + sum(travel) + sum(expense),
            }}
            totalHours={sumTime(consulting).hours + sumTime(travel).hours}
            totalMinutes={sumTime(consulting).minutes + sumTime(travel).minutes}
            hourLimit={invoice.hour_limit}
            company={company}
            csrfToken={csrfToken}
            statusChangeDate={invoice.status_change}
            location={location}
            feedback={invoice.feedback}
            updated={updated}
            editsRequestedBy={invoice.edits_requested_by}
            editsRequestedOn={invoice.edits_requested_on}
            approvedBy={invoice.approved_by}
            approvedOn={invoice.approved_on}
            workCommitment={workCommitment}
            areaOfExpertises={areaOfExpertises}
            tags={tags}
            jobTitles={jobTitles}
            description={description}
            responsibilities={responsibilities}
            duration={duration}
            workLocation={workLocation}
            shortOfficeLocation={shortOfficeLocation}
            startDate={startDate}
            buttonStyles="body-text-1"
            showHourWarning={showHourWarning}
          />
          <LineItems
            invoiceId={invoice.resource_id}
            lineItems={consulting}
            lineItemsGroupName={`Consulting Hours - $${invoice.labor_rate}/hr`}
            descriptionLabel="Task Description"
            category="consulting"
            hasDuration
            rate={invoice.labor_rate}
            startDate={invoice.start_date}
            endDate={invoice.end_date}
            handleNewLineItem={this.handleNewLineItem}
            handleUpdateLineItem={this.handleUpdateLineItem}
            handleDeleteLineItem={this.handleDeleteLineItem}
            totalPay={sum(consulting)}
            totalTime={sumTime(consulting)}
            status={invoice.status}
          />
          <LineItems
            invoiceId={invoice.resource_id}
            lineItems={travel}
            lineItemsGroupName={`Travel Hours - $${invoice.travel_rate}/hr`}
            descriptionLabel="Travel Description"
            category="travel"
            hasDuration
            rate={invoice.travel_rate}
            startDate={invoice.start_date}
            endDate={invoice.end_date}
            handleNewLineItem={this.handleNewLineItem}
            handleUpdateLineItem={this.handleUpdateLineItem}
            handleDeleteLineItem={this.handleDeleteLineItem}
            totalPay={sum(travel)}
            totalTime={sumTime(travel)}
            status={invoice.status}
          />
          <LineItems
            invoiceId={invoice.resource_id}
            lineItems={expense}
            lineItemsGroupName="Expenses"
            descriptionLabel="Expense Description"
            category="expense"
            startDate={invoice.start_date}
            endDate={invoice.end_date}
            handleNewLineItem={this.handleNewLineItem}
            handleUpdateLineItem={this.handleUpdateLineItem}
            handleDeleteLineItem={this.handleDeleteLineItem}
            handleUploadFile={this.handleUploadFile}
            totalPay={sum(expense)}
            csrfToken={csrfToken}
            status={invoice.status}
          />
        </div>
      </div>
    );
  }
}

Invoice.propTypes = {
  invoice: PropTypes.shape({}).isRequired,
  projectName: PropTypes.string.isRequired,
  projectResourceId: PropTypes.string.isRequired,
  company: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  location: PropTypes.string.isRequired,
  workCommitment: PropTypes.number.isRequired,
  areaOfExpertises: PropTypes.arrayOf(PropTypes.string),
  tags: PropTypes.arrayOf(PropTypes.string),
  jobTitles: PropTypes.string,
  description: PropTypes.string.isRequired,
  responsibilities: PropTypes.arrayOf(PropTypes.string).isRequired,
  duration: PropTypes.string.isRequired,
  workLocation: PropTypes.string.isRequired,
  shortOfficeLocation: PropTypes.string.isRequired,
  startDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
  showHourWarning: PropTypes.bool,
};

Invoice.defaultProps = {
  areaOfExpertises: [],
  tags: [],
  jobTitles: '',
  startDate: null,
  showHourWarning: false,
};

export default Invoice;
